/** 打印模板页脚：贴页面底部固定区域，并在打印时每页重复 */

export function resolvePrintFooterLayout (page) {
  const p = page || {}
  const fixed = p.footerFixedToPageBottom !== false
  const repeatEachPage = p.footerRepeatEachPage !== false
  const raw = Number(p.footerReservedHeightMm ?? 7)
  const heightMm =
    Number.isFinite(raw) && raw > 0 ? Math.min(25, Math.max(4, raw)) : 7
  return {
    fixed,
    repeatEachPage: fixed && repeatEachPage,
    heightMm,
    heightCss: `${heightMm}mm`
  }
}

export function printPageFooterCssVars (page) {
  const { heightMm } = resolvePrintFooterLayout(page)
  return { '--print-footer-height': `${heightMm}mm` }
}

export function useFixedPrintPageFooter (page, { disabled = false } = {}) {
  if (disabled) return false
  return resolvePrintFooterLayout(page).fixed
}

export function useRepeatPrintFooterEachPage (page, { disabled = false, hasFooter = true } = {}) {
  if (disabled || !hasFooter) return false
  return resolvePrintFooterLayout(page).repeatEachPage
}

/** @page margin 字符串；页脚重复时为正文预留底部页脚区高度 */
export function buildPageMarginCssString (page, { reserveFooter = true } = {}) {
  const p = page || {}
  const mt = Number(p.marginTop ?? 10) || 10
  const mr = Number(p.marginRight ?? 10) || 10
  const mb = Number(p.marginBottom ?? 10) || 10
  const ml = Number(p.marginLeft ?? 10) || 10
  const layout = resolvePrintFooterLayout(p)
  const bottom =
    reserveFooter && layout.repeatEachPage ? mb + layout.heightMm : mb
  return `${mt}mm ${mr}mm ${bottom}mm ${ml}mm`
}

/** 自动分页打印时，页码占位符改为 CSS 计数器（每页递增） */
export function injectPrintFlowPageCounters (html) {
  if (html == null || html === '') return ''
  return String(html)
    .replace(/\{page_number\}/g, '<span class="print-css-page-number"></span>')
    .replace(/\{total_pages\}/g, '<span class="print-css-total-pages"></span>')
}

/** 预览区占位；正式打印时由 counter(page)/counter(pages) 覆盖 */
export function buildPrintPageCounterCss () {
  return `
.print-css-page-number::before { content: '1'; }
.print-css-total-pages::before { content: '1'; }
@media print {
  .print-css-page-number::before { content: counter(page); }
  .print-css-total-pages::before { content: counter(pages); }
}
`
}

/** 单页贴底（预览 / 仅一页） */
export function buildFixedPrintFooterCss ({ heightMm = 7, bodyFontFamily = 'inherit' } = {}) {
  const h = `${heightMm}mm`
  return `
.print-page--fixed-footer {
  display: flex !important;
  flex-direction: column !important;
}
.print-page--fixed-footer .print-page__content-shell,
.print-page--fixed-footer .print-page__content {
  display: flex !important;
  flex-direction: column !important;
  flex: 1 1 auto !important;
  min-height: 0 !important;
  width: 100% !important;
  box-sizing: border-box !important;
}
.print-page--fixed-footer .print-body {
  flex: 1 1 auto !important;
  min-height: 0 !important;
}
.print-page--fixed-footer .print-footer {
  flex: 0 0 ${h} !important;
  height: ${h} !important;
  min-height: ${h} !important;
  max-height: ${h} !important;
  margin-top: auto !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
  justify-content: flex-end !important;
  align-items: stretch !important;
  box-sizing: border-box !important;
  padding: 0 10px !important;
  border-top: 1px solid #eee !important;
  font-family: ${bodyFontFamily};
  page-break-inside: avoid !important;
  break-inside: avoid !important;
}
.print-page--fixed-footer .print-footer-line {
  flex-shrink: 0;
}
.preview-table-page--with-footer {
  display: flex !important;
  flex-direction: column !important;
}
.preview-table-page--with-footer .print-table {
  flex: 0 0 auto;
}
.preview-table-page--with-footer .print-page-footer-slot {
  flex: 0 0 var(--print-footer-height, 7mm);
  height: var(--print-footer-height, 7mm);
  min-height: var(--print-footer-height, 7mm);
  max-height: var(--print-footer-height, 7mm);
  margin-top: auto !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  box-sizing: border-box;
  padding: 0 10px;
  border-top: 1px solid #eee;
}
.pure-print-page .pure-print-footer {
  flex: 0 0 ${h} !important;
  height: ${h} !important;
  min-height: ${h} !important;
  max-height: ${h} !important;
  vertical-align: bottom !important;
}
`
}

/** 打印时每页重复页脚（position: fixed + 预留 @page 底边距） */
export function buildRepeatEachPagePrintFooterCss ({
  heightMm = 7,
  bodyFontFamily = 'inherit',
  footerAlign = 'left',
  marginBottomMm = 10
} = {}) {
  const h = `${heightMm}mm`
  return `
@media print {
  .print-footer.print-footer--repeat-each-page {
    position: fixed !important;
    left: 0 !important;
    right: 0 !important;
    bottom: ${marginBottomMm}mm !important;
    width: auto !important;
    height: ${h} !important;
    min-height: ${h} !important;
    max-height: ${h} !important;
    margin: 0 !important;
    padding: 0 10px !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: flex-end !important;
    align-items: stretch !important;
    text-align: ${footerAlign} !important;
    box-sizing: border-box !important;
    background: #fff !important;
    border-top: 1px solid #eee !important;
    font-family: ${bodyFontFamily};
    z-index: 9999 !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .print-footer.print-footer--repeat-each-page .print-footer-line {
    flex-shrink: 0;
  }
}
`
}
